import { ArrowRight, Clock, Inbox, Sparkles } from 'lucide-react';
import { useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  useGetParentCatalogQuery,
  useLeaveWaitlistMutation,
  useListParentWaitlistQuery,
} from '../../../state/domainRouterApi';
import { mapCampWaitlistListResponse, mapParentCatalogResponse } from '../camps/viewModels';
import { ModuleSectionNav, type ModuleSectionNavItem } from '../../../components/navigation/ModuleSectionNav';
import { SupportErrorMessage } from '../../../components/overlays/EngineeringSupportBanner';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function statusLabel(value: string | undefined): string {
  switch (value) {
    case 'waiting':
      return 'Waiting';
    case 'offered':
      return 'Spot offered';
    case 'expired':
      return 'Expired';
    default:
      return value ?? '--';
  }
}

function statusDotColor(value: string | undefined): string {
  switch (value) {
    case 'waiting':
      return 'var(--coral)';
    case 'offered':
      return 'var(--aquamarine)';
    case 'expired':
      return 'var(--iron-grey)';
    default:
      return 'var(--iron-grey)';
  }
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="h-8 w-48 animate-pulse rounded-lg bg-gray-100" />
      <div className="h-10 w-64 animate-pulse rounded-full bg-gray-100" />
      <div className="h-16 animate-pulse rounded-xl bg-gray-100" />
      <div className="space-y-4">
        {[1, 2].map((i) => (
          <div key={i} className="h-28 animate-pulse rounded-xl bg-gray-100" />
        ))}
      </div>
    </div>
  );
}

function ErrorBanner({ title, detail }: { title: string; detail: string }) {
  return (
    <SupportErrorMessage>
      <p className="font-semibold text-irongrey">{title}</p>
      <p className="mt-1 text-sm text-irongrey/65">{detail}</p>
    </SupportErrorMessage>
  );
}

function TabNav({ active }: { active: 'catalog' | 'waitlist' | 'registrations' }) {
  const items: ModuleSectionNavItem[] = [
    { id: 'catalog', label: 'Catalog', to: '/parent/portal/camps', end: true },
    { id: 'waitlist', label: 'Waitlist', to: '/parent/portal/camps/waitlist' },
    { id: 'registrations', label: 'Registrations', to: '/parent/portal/camps/registrations' },
  ];

  return (
    <ModuleSectionNav
      items={items}
      activeId={active}
      ariaLabel="Camps navigation"
      testIds={{
        root: 'camps-tab-nav',
        itemPrefix: 'camps-tab-nav-item',
        mobileTrigger: 'camps-tab-nav-mobile-trigger',
        mobileDialog: 'camps-tab-nav-mobile-dialog',
        mobileItemPrefix: 'camps-tab-nav-mobile-item',
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Waitlist Entry Card
// ---------------------------------------------------------------------------

function WaitlistEntryCard({
  entry,
  onLeave,
  isLeaving,
}: {
  entry: ReturnType<typeof mapCampWaitlistListResponse>['items'][number];
  onLeave: (id: string) => void;
  isLeaving: boolean;
}) {
  return (
    <div className="rounded-xl border border-border bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1 space-y-1">
          <h3 className="font-semibold text-iron-grey">{entry.programName ?? 'Camp Program'}</h3>
          {entry.sessionLabel ? (
            <p className="text-sm text-text-secondary">{entry.sessionLabel}</p>
          ) : null}
          {entry.studentName ? (
            <p className="text-sm text-text-secondary">Camper: {entry.studentName}</p>
          ) : null}
        </div>

        <div className="flex flex-col items-end gap-3 sm:min-w-[160px]">
          {entry.position != null ? (
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-text-secondary" aria-hidden="true" />
              <span className="text-sm font-medium text-iron-grey">Position #{entry.position}</span>
            </div>
          ) : null}

          <div className="flex items-center gap-1.5">
            <span
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: statusDotColor(entry.status) }}
            />
            <span className="text-sm font-medium text-iron-grey">{statusLabel(entry.status)}</span>
          </div>

          {entry.status !== 'expired' ? (
            <button
              type="button"
              disabled={isLeaving}
              onClick={() => onLeave(entry.waitlistEntryId ?? '')}
              className="rounded-lg border border-[var(--strawberry)] px-4 py-2 text-sm font-semibold text-[var(--strawberry)] transition-colors hover:bg-[var(--strawberry)]/5 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isLeaving ? 'Leaving...' : 'Leave Waitlist'}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page Component
// ---------------------------------------------------------------------------

export function ParentCampsWaitlistPage() {
  const { data: rawData, isLoading, isError, error } = useListParentWaitlistQuery();
  const { data: rawCatalog, isLoading: isCatalogLoading } = useGetParentCatalogQuery();
  const [leaveWaitlist] = useLeaveWaitlistMutation();
  const [leavingId, setLeavingId] = useState<string | null>(null);
  const data = mapCampWaitlistListResponse(rawData, rawCatalog);
  const catalogItems = mapParentCatalogResponse(rawCatalog).items;
  const entries = data.items;
  const totalCount = data.totalCount;
  const positionedEntries = entries.flatMap((entry) => (entry.position != null ? [entry.position] : []));
  const earliestPosition = positionedEntries.length > 0 ? Math.min(...positionedEntries) : null;

  const alternatePrograms = useMemo(() => {
    const waitlistedProgramNames = new Set(
      entries.map((entry) => entry.programName).filter(Boolean),
    );
    return catalogItems
      .filter(
        (program) =>
          (program.spotsRemaining ?? 0) > 0
          && program.registrationStatus !== 'closed'
          && !waitlistedProgramNames.has(program.name ?? ''),
      )
      .slice(0, 4);
  }, [catalogItems, entries]);

  const handleLeave = async (waitlistEntryId: string) => {
    setLeavingId(waitlistEntryId);
    try {
      await leaveWaitlist({ waitlistEntryId }).unwrap();
    } catch {
      // Error is surfaced via RTK cache invalidation / refetch
    } finally {
      setLeavingId(null);
    }
  };

  if (isLoading) {
    return (
      <main
        data-testid="parent-camps-waitlist"
        data-route-ready="true"
        data-extracted-from="source-faithful:parent-camps-v1.0"
        className="bg-background-light"
      >
        <div className="w-full space-y-6">
          <LoadingSkeleton />
        </div>
      </main>
    );
  }

  if (isError) {
    return (
      <main
        data-testid="parent-camps-waitlist"
        data-route-ready="true"
        data-extracted-from="source-faithful:parent-camps-v1.0"
        className="bg-background-light"
      >
        <div className="w-full space-y-6">
          <ErrorBanner
            title="Unable to load waitlist"
            detail={
              (error && typeof error === 'object' && 'message' in error
                ? String((error as { message?: unknown }).message)
                : null) ??
              'Waitlist data is not available right now. Please try again later.'
            }
          />
        </div>
      </main>
    );
  }

  return (
    <main
      data-testid="parent-camps-waitlist"
      data-route-ready="true"
      data-extracted-from="source-faithful:parent-camps-v1.0"
      className="bg-background-light"
    >
      <div className="w-full space-y-6">
        {/* Page header */}
        <div data-testid="camps-waitlist-header">
          <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--primary)]">
            Parent portal
          </p>
          <h1 className="text-[24px] font-semibold text-iron-grey">Camp Waitlist</h1>
        </div>

        {/* Tab navigation */}
        <TabNav active="waitlist" />

        {/* Waitlist summary */}
        <div
          data-testid="camps-waitlist-summary"
          className="rounded-xl border border-border bg-white p-5 shadow-sm"
        >
          <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.05em] text-text-secondary">
                Total entries
              </p>
              <p className="text-lg font-semibold text-iron-grey">{totalCount}</p>
            </div>
            {earliestPosition != null && Number.isFinite(earliestPosition) ? (
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.05em] text-text-secondary">
                  Best position
                </p>
                <p className="text-lg font-semibold text-iron-grey">#{earliestPosition}</p>
              </div>
            ) : null}
          </div>
        </div>

        {/* Entries list or empty state */}
        {entries.length === 0 ? (
          <div
            data-testid="camps-waitlist-empty"
            className="rounded-xl border border-border bg-white py-16 text-center shadow-sm"
          >
            <Inbox className="mx-auto h-10 w-10 text-text-secondary" aria-hidden="true" />
            <p className="mt-4 font-semibold text-iron-grey">No waitlist entries</p>
            <p className="mt-1 text-sm text-text-secondary">
              You are not on any camp waitlists. Browse the{' '}
              <NavLink to="/parent/portal/camps" className="font-medium text-[var(--primary)] hover:underline">
                catalog
              </NavLink>{' '}
              to find available programs.
            </p>
          </div>
        ) : (
          <div data-testid="camps-waitlist-entries" className="space-y-4">
            {entries.map((entry) => (
              <WaitlistEntryCard
                key={entry.waitlistEntryId ?? `${entry.programName}-${entry.studentName}`}
                entry={entry}
                onLeave={(id) => { void handleLeave(id); }}
                isLeaving={leavingId === entry.waitlistEntryId}
              />
            ))}
          </div>
        )}

        {/* Position explanation */}
        {entries.length > 0 ? (
          <div className="rounded-lg border border-border bg-white p-4 shadow-sm">
            <p className="text-sm text-text-secondary">
              Your waitlist position indicates how many families are ahead of you.
              When a spot opens, the family in position #1 is notified first. You will
              receive an email when a spot becomes available for your child.
            </p>
          </div>
        ) : null}

        {/* Gap 2: Open Alternatives — cross-query from catalog */}
        {entries.length > 0 && !isCatalogLoading && alternatePrograms.length > 0 ? (
          <div data-testid="camps-waitlist-alternatives">
            <div className="mb-3 flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[var(--primary)]" aria-hidden="true" />
              <h2 className="text-sm font-semibold text-iron-grey">Open Alternatives</h2>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {alternatePrograms.map((p) => (
                <div
                  key={p.programId ?? p.name}
                  className="rounded-xl border border-border bg-white p-4 shadow-sm"
                >
                  <h3 className="font-semibold text-iron-grey">{p.name ?? 'Camp Program'}</h3>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-text-secondary">
                    {p.gradeRange ? <span>{p.gradeRange}</span> : null}
                    {p.spotsRemaining != null ? (
                      <span className="flex items-center gap-1">
                        <span
                          className="h-1.5 w-1.5 rounded-full"
                          style={{ backgroundColor: 'var(--aquamarine)' }}
                        />
                        {p.spotsRemaining} spot{p.spotsRemaining !== 1 ? 's' : ''}
                      </span>
                    ) : null}
                  </div>
                  <NavLink
                    to={`/parent/portal/camps`}
                    className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-[var(--primary)] hover:underline"
                  >
                    View <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                  </NavLink>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </main>
  );
}
